"use client";

import { c as _c } from "react/compiler-runtime";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { SecureStoragePlugin } from "capacitor-secure-storage-plugin";
import { ethers } from "ethers";
import { encryptData } from "./app-wallet-helpers";
import { Time } from "@/helpers/time";
import useCapacitor from "@/hooks/useCapacitor";
export interface AppWallet {
  name: string;
  created_at: number;
  address: string;
  address_hashed: string;
  mnemonic: string;
  private_key: string;
  imported: boolean;
}
interface AppWalletsContextProps {
  fetchingAppWallets: boolean;
  appWallets: AppWallet[];
  appWalletsSupported: boolean;
  createAppWallet: (name: string, pass: string) => Promise<boolean>;
  importAppWallet: (walletName: string, walletPass: string, address: string, mnemonic: string, privateKey: string) => Promise<boolean>;
  deleteAppWallet: (address: string) => Promise<boolean>;
}
const AppWalletsContext = createContext<AppWalletsContextProps | undefined>(undefined);
const WALLET_KEY_PREFIX = "app-wallet_";
export const AppWalletsProvider: React.FC<{
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(25);
  const {
    children
  } = t0;
  const [fetchingAppWallets, setFetchingAppWallets] = useState(true);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [appWallets, setAppWallets] = useState(t1);
  const [appWalletsSupported, setAppWalletsSupported] = useState(false);
  const capacitor = useCapacitor();
  const {
    isCapacitor
  } = capacitor;
  let t2;
  if ($[1] !== appWalletsSupported) {
    t2 = async () => {
      if (!appWalletsSupported) {
        setFetchingAppWallets(false);
        setAppWallets([]);
        return;
      }
      setFetchingAppWallets(true);
      const wallets = await getAllWallets();
      setAppWallets(wallets);
      setFetchingAppWallets(false);
    };
    $[1] = appWalletsSupported;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const fetchAppWallets = t2;
  let t3;
  let t4;
  if ($[3] !== isCapacitor) {
    t3 = () => {
      let cancelled = false;
      const initialize = async () => {
        let supported = false;
        if (isCapacitor) {
          ;
          try {
            await SecureStoragePlugin.keys();
            supported = true;
          } catch (t5) {
            const error = t5;
            console.error("SecureStoragePlugin is not available:", error);
          }
        }
        if (cancelled) {
          return;
        }
        setAppWalletsSupported(supported);
        if (!supported) {
          setAppWallets([]);
          setFetchingAppWallets(false);
          return;
        }
        setFetchingAppWallets(true);
        const wallets_0 = await getAllWallets();
        if (cancelled) {
          return;
        }
        setAppWallets(wallets_0);
        setFetchingAppWallets(false);
      };
      initialize();
      return () => {
        cancelled = true;
      };
    };
    t4 = [isCapacitor];
    $[3] = isCapacitor;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== appWalletsSupported || $[7] !== fetchAppWallets) {
    t5 = async (name, pass) => {
      if (!appWalletsSupported) {
        return false;
      }
      const wallet = ethers.Wallet.createRandom();
      const encryptedAddress = await encryptData(wallet.address, wallet.address, pass);
      const encryptedMnemonic = await encryptData(wallet.address, wallet.mnemonic?.phrase ?? "", pass);
      const encryptedPrivateKey = await encryptData(wallet.address, wallet.privateKey, pass);
      const appWallet = {
        name,
        created_at: Time.now().toSeconds(),
        address: wallet.address,
        address_hashed: encryptedAddress,
        mnemonic: encryptedMnemonic,
        private_key: encryptedPrivateKey,
        imported: false
      };
      const result = await SecureStoragePlugin.set({
        key: `${WALLET_KEY_PREFIX}${wallet.address}`,
        value: JSON.stringify(appWallet)
      });
      await fetchAppWallets();
      return result.value;
    };
    $[6] = appWalletsSupported;
    $[7] = fetchAppWallets;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const createAppWallet = t5;
  let t6;
  if ($[9] !== appWalletsSupported || $[10] !== fetchAppWallets) {
    t6 = async (walletName, walletPass, address, mnemonic, privateKey) => {
      if (!appWalletsSupported) {
        return false;
      }
      const encryptedAddress_0 = await encryptData(address, address, walletPass);
      const encryptedMnemonic_0 = await encryptData(address, mnemonic, walletPass);
      const encryptedPrivateKey_0 = await encryptData(address, privateKey, walletPass);
      const wallet_0 = {
        name: walletName,
        created_at: Time.now().toSeconds(),
        address,
        address_hashed: encryptedAddress_0,
        mnemonic: encryptedMnemonic_0,
        private_key: encryptedPrivateKey_0,
        imported: true
      };
      const result_0 = await SecureStoragePlugin.set({
        key: `${WALLET_KEY_PREFIX}${address}`,
        value: JSON.stringify(wallet_0)
      });
      await fetchAppWallets();
      return result_0.value;
    };
    $[9] = appWalletsSupported;
    $[10] = fetchAppWallets;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const importAppWallet = t6;
  let t7;
  if ($[12] !== appWalletsSupported || $[13] !== fetchAppWallets) {
    t7 = async address_0 => {
      if (!appWalletsSupported) {
        return false;
      }
      const result_1 = await SecureStoragePlugin.remove({
        key: `${WALLET_KEY_PREFIX}${address_0}`
      });
      await fetchAppWallets();
      return result_1.value;
    };
    $[12] = appWalletsSupported;
    $[13] = fetchAppWallets;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const deleteAppWallet = t7;
  let t8;
  if ($[15] !== appWallets || $[16] !== appWalletsSupported || $[17] !== createAppWallet || $[18] !== deleteAppWallet || $[19] !== fetchingAppWallets || $[20] !== importAppWallet) {
    t8 = {
      fetchingAppWallets,
      appWallets,
      appWalletsSupported,
      createAppWallet,
      importAppWallet,
      deleteAppWallet
    };
    $[15] = appWallets;
    $[16] = appWalletsSupported;
    $[17] = createAppWallet;
    $[18] = deleteAppWallet;
    $[19] = fetchingAppWallets;
    $[20] = importAppWallet;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const value = t8;
  let t9;
  if ($[22] !== children || $[23] !== value) {
    t9 = <AppWalletsContext.Provider value={value}>{children}</AppWalletsContext.Provider>;
    $[22] = children;
    $[23] = value;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
};
export const useAppWallets = () => {
  const context = useContext(AppWalletsContext);
  if (!context) {
    throw new Error("useAppWallets must be used within an AppWalletsProvider");
  }
  return context;
};
const getAllWallets = async () => {
  let wallets: AppWallet[] = [];
  try {
    const keysResult = await SecureStoragePlugin.keys();
    const walletKeys = keysResult.value.filter(key => key.startsWith(WALLET_KEY_PREFIX));
    const walletValues = await Promise.all(walletKeys.map(async key => {
      const valueResult = await SecureStoragePlugin.get({
        key
      });
      return JSON.parse(valueResult.value);
    }));
    wallets = walletValues.sort((a, b) => a.created_at - b.created_at);
  } catch (error) {
    console.error("Error fetching wallets:", error);
  }
  return wallets;
};